/** In-memory editing state. Sensitive registration details must never enter browser storage. */
export type DraftEditing = {
  baseline: Record<string, string>;
  values: Record<string, string>;
  conflicts: Record<string, string>;
};

export function beginDraft(values: Record<string, string>): DraftEditing {
  return { baseline: values, values, conflicts: {} };
}

/** Merge fresh server values without overwriting local edits or unresolved conflicts. */
export function reconcileDraft(
  current: DraftEditing,
  saved: Record<string, string>,
  submitted?: { entered: Record<string, string>; normalized: Record<string, string> },
): DraftEditing {
  const values: Record<string, string> = {};
  const conflicts: Record<string, string> = {};
  for (const key of new Set([...Object.keys(current.baseline), ...Object.keys(current.values), ...Object.keys(saved)])) {
    const before = current.baseline[key] || "";
    const local = current.values[key] || "";
    const remote = saved[key] || "";
    const unresolved = Object.hasOwn(current.conflicts, key);
    if (local === remote) values[key] = remote;
    else if (unresolved) {
      values[key] = local;
      conflicts[key] = remote;
    // An ambiguous save may have completed after the user edited again (even back to the baseline).
    } else if (submitted && Object.hasOwn(submitted.entered, key) && submitted.entered[key] !== local && remote !== before) {
      values[key] = local;
      conflicts[key] = remote;
    } else if (local === before) values[key] = remote;
    else if (remote === before) values[key] = local;
    // A lost response may already have saved the submitted, normalized entry.
    else if (submitted && Object.hasOwn(submitted.normalized, key) && submitted.normalized[key] === remote && submitted.entered[key] === local) values[key] = remote;
    else {
      values[key] = local;
      conflicts[key] = remote;
    }
  }
  return { baseline: saved, values, conflicts };
}

export function resolveDraft(current: DraftEditing, key: string, useSaved: boolean): DraftEditing {
  const conflicts = { ...current.conflicts };
  delete conflicts[key];
  return { ...current, values: useSaved ? { ...current.values, [key]: current.baseline[key] || "" } : current.values, conflicts };
}
